import { inject, Injectable } from "@angular/core";
import { Actions, ofType } from "@ngrx/effects";
import { ActionsSubject } from "@ngrx/store";
import { AuthActions, createFrom, EventService, isNotNullable, UserActions, UserIdService } from "@spartacus/core";
import { UserAccountFacade } from '@spartacus/user/account/root';
import { sha256 } from 'js-sha256';
import { Observable, of } from "rxjs";
import { filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import {
  GuestFriendsActionTypes,
} from "../../../../../custom/cms-components/myaccount/custom-friends/components/custom-register-friend-form/store/actions/custom-friends-guest.action";
import {
  FriendsActionTypes,
} from '../../../../custom/core/custom-friends/custom-friends-users/store/actions/custom-friends-users.action';
import { CustomCleanEcommerceEvent } from "../common/custom-common.events";
import { EcommerceType } from "../tag-manager-feature.model";
import {
  CustomGuestFriendRegisterEvent,
  CustomUserAddFriendCompleteEvent,
  CustomUserAddFriendStartEvent,
  CustomUserAddProductInterestEvent,
  CustomUserLoginEvent,
  CustomUserRegisterEvent,
} from "./custom-user.events";

@Injectable({
  providedIn: 'root',
})
export class CustomUserEventBuilder {
  protected readonly actions = inject(Actions);
  protected readonly actionsSubject = inject(ActionsSubject);
  protected readonly eventService = inject(EventService);
  protected readonly userIdService = inject(UserIdService);
  protected readonly userAccount = inject(UserAccountFacade);
  protected currentUid?: string;

  constructor() {
    this.register();
  }

  protected register(): void {
    this.registerAddProductInterestEvents();
    this.registerUserLoginEvents();
    this.registerUserRegisterEvents();
    this.registerGuestFriendRegisterEvents();
    this.registerAddFriendEvents();
  }

  protected registerAddProductInterestEvents(): void {
    this.eventService.register(
      CustomUserAddProductInterestEvent,
      this.buildAddProductInterestEvent()
    );
  }

  protected registerUserLoginEvents(): void {
    this.eventService.register(
      CustomUserLoginEvent,
      this.registerUserLogin(),
    );
  }

  protected registerUserRegisterEvents(): void {
    this.eventService.register(
      CustomUserRegisterEvent,
      this.registerUserRegister(),
    );
  }

  protected registerGuestFriendRegisterEvents(): void {
    this.eventService.register(
      CustomGuestFriendRegisterEvent,
      this.registerGuestFriendRegister(),
    );
  }

  protected registerAddFriendEvents(): void {
    this.eventService.register(
      CustomUserAddFriendStartEvent,
      this.registerAddFriendStart(),
    )

    this.eventService.register(
      CustomUserAddFriendCompleteEvent,
      this.registerAddFriendComplete(),
    )
  }

  protected registerUserLogin(): Observable<CustomUserLoginEvent> {
    return this.actionsSubject.pipe(
      filter((action) => action.type === AuthActions.LOGIN),
      switchMap(() =>
        this.userAccount.get().pipe(
          filter(isNotNullable),
          tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ ecommerce: null }, CustomCleanEcommerceEvent)),
          map((user) => createFrom(CustomUserLoginEvent, {
            event: EcommerceType.LOGIN,
            method: 'email',
            uid: sha256(user.uid),
            userId: user.customerId,
            userGroupId: user.exclusiveRole,
          })),
        ),
      ),
    );
  }

  protected registerUserRegister(): Observable<CustomUserRegisterEvent> {
    return this.actionsSubject.pipe(
      ofType(UserActions.REGISTER_USER_SUCCESS),
      tap( () => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map(() => createFrom(CustomUserRegisterEvent, {
        event: EcommerceType.SIGN_UP,
        method: 'email',
        uid: this.currentUid,
      }))
    );
  }

  protected registerGuestFriendRegister(): Observable<CustomGuestFriendRegisterEvent> {
    return this.actionsSubject.pipe(
      ofType(GuestFriendsActionTypes.CREATE_GUEST_FRIEND_SUCCESS),
      tap( () => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => createFrom(CustomGuestFriendRegisterEvent, {
        event: EcommerceType.SIGN_UP,
        method: 'email',
        uid: action.payload.uid,
      }))
    );
  }

  protected buildAddProductInterestEvent(): Observable<CustomUserAddProductInterestEvent> {
    return this.actionsSubject.pipe(
      ofType(UserActions.ADD_PRODUCT_INTEREST_SUCCESS),
      switchMap((action: any) => of(action.payload.product)),
      withLatestFrom(this.userAccount.get()),
      map(([product, user]) => {
        return createFrom(CustomUserAddProductInterestEvent, {
          event: EcommerceType.OUT_OF_STOCK,
          item_name: product.name,
          userGroupId: user?.exclusiveRole || '',
        })
      })
    );
  }

  protected registerAddFriendStart(): Observable<CustomUserAddFriendStartEvent> {
    return this.actions.pipe(
      ofType(FriendsActionTypes.START_CREATE_FRIEND),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map(() => createFrom(CustomUserAddFriendStartEvent, {
        event: EcommerceType.ADD_FRIEND_START,
      }))
    );
  }

  protected registerAddFriendComplete(): Observable<CustomUserAddFriendCompleteEvent> {
    return this.actions.pipe(
      ofType(FriendsActionTypes.CREATE_FRIEND_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map(action => createFrom(CustomUserAddFriendCompleteEvent, {
        event: EcommerceType.ADD_FRIEND_COMPLETE,
        uid: sha256(action.payload?.friend?.uid),
      }))
    );
  }

  setCurrentUid(uid: string) {
    this.currentUid = uid;
  }
}
